import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  ColumnDef,
  DropdownOption,
  Heading,
  Input,
  Link,
  RyeconMagnifyingGlass,
} from '@dbt-labs/sourdough';

import { AppliedModelSortField, SortDirection } from '@/__generated__/graphql';
import { PaginatedTable } from '@/components/PaginatedTable';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import {
  ModelAppliedListNode,
  ModelWithQueryCount,
  useDiscoveryAppliedModels,
} from '@/hooks/discovery/appliedModels';
import { useAllMarts } from '@/hooks/discovery/marts';
import { useModelQueryHistoryState } from '@/hooks/discovery/modelQueryHistoryState';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { SearchNode, useProjectLevelSearchResults } from '@/hooks/useFuzzySearch';
import { useMartsColumns, useMartsColumnsNew } from '../hooks/useMartsColumns';

const PAGE_SIZE = 5;

const pageOptions: DropdownOption[] = [
  { label: '5 Rows', value: 5 },
  { label: '10 Rows', value: 10 },
  { label: '25 Rows', value: 25 },
  { label: '50 Rows', value: 50 },
];

export const Marts = () => {
  const { accountId, projectId, environmentId, enableModelQueryHistory } =
    useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const { environmentType } = useUrlProjectParams();
  const getModelFilterLink = useLinkToExplorer('resourceFilter');

  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = PAGE_SIZE;
  const [currentPage, setCurrentPage] = useState(0);

  const marts = useAllMarts({
    environmentId,
    userId,
    includeQueryCount: enableModelQueryHistory,
  });

  const martNodes = useMemo(
    (): SearchNode[] | undefined =>
      marts.data?.map((model: ModelAppliedListNode) => ({
        projectId: model.projectId,
        environmentId: model.environmentId,
        uniqueId: model.uniqueId,
        name: model.name,
        modelingLayer: model.modelingLayer,
        tags: model.tags,
        packageName: model.packageName,
        group: model.group,
        access: model.access,
        hasAccess: true,
      })),
    [marts.data],
  );

  const martsQuery = useProjectLevelSearchResults<SearchNode>({
    query: searchQuery,
    nodes: martNodes || [],
    pageSize,
    paginationType: 'pageBased',
    currentPage,
  });

  const disableGroupsColumn =
    !!martsQuery.data && !martsQuery.data.some((model) => !!model.group);

  const columns = useMartsColumnsNew({ disableGroupsColumn });

  return (
    <div className="my-10">
      <div className="flex">
        <div className="mb-1 flex-1">
          <Heading size="4">Marts</Heading>
        </div>
        <Link
          isInternal
          to={getModelFilterLink({
            accountId,
            environmentType,
            projectId,
            resourceType: 'model',
            sort: {
              direction: SortDirection.Desc,
              field: AppliedModelSortField.QueryUsageCount,
            },
          })}
          className="flex-0"
        >
          See all models
        </Link>
      </div>
      <div>
        <Input
          name="mart-search"
          placeholder="Find a mart"
          labelIsHidden
          isEdit
          startIcon={{ ryecon: RyeconMagnifyingGlass }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`my-1 w-full max-w-md flex-1 px-1`}
        />
      </div>
      <div className="mt-4">
        <MartsTable
          columns={columns}
          marts={martsQuery.data ?? []}
          fetching={marts.fetching}
          pageSize={pageSize}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalCount={martsQuery.totalCount}
        />
      </div>
    </div>
  );
};

/** A thin wrapper around the MartsTable, for the Legacy Project Overview page*/
export const MartsSection = () => {
  const { environmentId, enableModelQueryHistory } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();

  const pageSize = PAGE_SIZE;

  const martsQuery = useDiscoveryAppliedModels({
    first: PAGE_SIZE,
    environmentId,
    userId,
    filter: {
      modelingLayer: 'marts',
    },
    sort: {
      field: AppliedModelSortField.QueryUsageCount,
      direction: SortDirection.Desc,
    },
    includeQueryCount: enableModelQueryHistory,
  });

  const [queryHistoryStateQuery] = useModelQueryHistoryState({ environmentId });
  const queryHistoryStateHolder =
    queryHistoryStateQuery.data?.environment.modelQueryHistoryState;
  const adapterType = queryHistoryStateQuery.data?.environment.adapterType;

  const disableGroupsColumn =
    !!martsQuery.data && !martsQuery.data.some((model) => !!model.group);

  const columns = useMartsColumns(
    { disableGroupsColumn },
    queryHistoryStateHolder,
    adapterType || '',
  );

  return (
    <div>
      <MartsTable
        columns={columns}
        marts={martsQuery.data ?? []}
        fetching={martsQuery.fetching}
        pageSize={pageSize}
      />
    </div>
  );
};

type MartsTableProps = {
  columns: ColumnDef<any>[];
  marts: (ModelWithQueryCount | SearchNode)[];
  fetching: boolean;
  pageSize: number;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  totalCount?: number;
};

export const MartsTable: FC<MartsTableProps> = ({
  columns,
  marts,
  fetching,
  pageSize,
  currentPage,
  setCurrentPage,
  totalCount,
}) => {
  const { accountId, projectId } = useDbtCloudProject();
  const getModelFilterUrl = useLinkToExplorer('resourceFilter');
  const navigate = useNavigate();

  const paginationType =
    currentPage !== undefined && setCurrentPage !== undefined
      ? 'pageBased'
      : 'loadMore';

  return (
    <div>
      <PaginatedTable
        columns={columns}
        data={marts ?? []}
        isLoading={fetching}
        maxResultCount={totalCount}
        pageSize={pageSize}
        paginationType={paginationType}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pagination={{
          selectedPageCount: pageOptions.find(({ value }) => value === pageSize),
          pageOptions,
          loadMoreLabel: 'See All Marts Models In This Project',
          hasMorePages: true,
          isPageLoading: fetching,
          onLoadMore: () => {
            navigate(
              getModelFilterUrl({
                accountId,
                projectId,
                environmentType: 'production',
                resourceType: 'model',
                modelingLayer: 'marts',
              }),
            );
          },
        }}
        emptyStateProps={{
          header: `There are no marts defined by this project`,
        }}
      />
    </div>
  );
};
